import React from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import FreeShipBar from './FreeShipBar';
import { activeSale, withSale } from '../lib/sale';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, count, discount, freeAwayCount } = useCart();
  const salePrice = (i) => withSale(i.price, activeSale(i));
  const saleSubtotal = items.reduce((s, i) => s + salePrice(i) * i.quantity, 0);

  return (
    <div className={`fixed inset-0 z-[70] ${isOpen ? 'visible' : 'invisible'}`}>
      <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsOpen(false)} />
      <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#f7f3ec] flex flex-col transition-transform duration-400 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#c6a15b]/25 bg-[#0b0b0b]">
          <h3 className="font-display text-2xl text-[#f7f3ec]">Your Bag <span className="text-[#c6a15b] text-lg">({count})</span></h3>
          <button onClick={() => setIsOpen(false)} className="text-[#e7d3a1] hover:opacity-70"><X size={24} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <ShoppingBag size={40} className="text-[#c6a15b] mb-4" />
            <p className="font-display text-2xl text-[#1a1a1a] mb-2">Your bag is empty</p>
            <p className="font-body text-sm text-[#6b6459] mb-6">Discover our luxury collections.</p>
            <Link to="/shop" onClick={() => setIsOpen(false)} className="btn-gold px-8 py-3.5"><span>Shop Now</span></Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.key} className="flex gap-4">
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-[#ece5d8]" />
                  <div className="flex-1">
                    <h4 className="font-display text-lg text-[#1a1a1a] leading-tight">{item.name}</h4>
                    <p className="font-body text-xs text-[#6b6459] mt-0.5">
                      {[item.length, item.density, item.color].filter(Boolean).join(' · ')}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#c6a15b]/40">
                        <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="px-2 py-1 text-[#6b6459] hover:text-[#0b0b0b]"><Minus size={13} /></button>
                        <span className="px-3 font-body text-sm">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="px-2 py-1 text-[#6b6459] hover:text-[#0b0b0b]"><Plus size={13} /></button>
                      </div>
                      {activeSale(item) ? (
                        <span className="flex items-baseline gap-1.5">
                          <span className="text-[#a39c8e] line-through text-xs">${(item.price * item.quantity).toFixed(2)}</span>
                          <span className="text-emerald-600 font-semibold text-sm">${(salePrice(item) * item.quantity).toFixed(2)}</span>
                        </span>
                      ) : (
                        <span className="font-body text-sm text-[#a8823c]">${(item.price * item.quantity).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.key)} className="text-[#a39c8e] hover:text-[#0b0b0b] self-start"><X size={16} /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-[#c6a15b]/25 px-6 py-5 bg-white">
              <FreeShipBar />
              {freeAwayCount > 0 && (
                <p className="font-body text-xs text-[#a8823c] mb-3 text-center" data-testid="drawer-braid-nudge">
                  Add {freeAwayCount} more braiding pack{freeAwayCount > 1 ? 's' : ''} to get 1 FREE!
                </p>
              )}
              {discount > 0 && (
                <div className="flex items-center justify-between mb-2" data-testid="drawer-braid-discount">
                  <span className="overline text-[#a8823c]">Buy 4, Get 1 Free</span>
                  <span className="font-body text-sm text-[#a8823c]">−${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className="overline text-[#6b6459]">Subtotal</span>
                <span className="font-display text-2xl text-[#1a1a1a]">${(saleSubtotal - discount).toFixed(2)}</span>
              </div>
              <p className="font-body text-xs text-[#6b6459] mb-4 text-center">Flat $9.99 shipping · taxes at checkout.</p>
              <Link to="/cart" onClick={() => setIsOpen(false)} className="btn-gold w-full py-4 flex items-center justify-center"><span>Checkout</span></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
